import { RemoveReadonly } from "@/helpers/typeHelper"
import {
  IMeterpoint,
  IBulkSmartReadsResult,
} from "@/models/meterpoint/meterpoint.types"
import { BillMutator } from "@/mutators/bill/BillMutator"
import { ConsentMutator } from "@/mutators/consent/ConsentMutator"
import { ContactMutator } from "@/mutators/contact/ContactMutator"
import { ImbpMutator } from "@/mutators/imbp/ImbpMutator"
import { JuniferMutator } from "@/mutators/junifer/JuniferMutator"
import { JuniferTicketMutator } from "@/mutators/juniferTicket/JuniferTicketMutator"
import { MeterReadingsMutator } from "@/mutators/meterReadings/MeterReadingsMutator"
import { NoteMutator } from "@/mutators/note/NoteMutator"
import { ReviewPeriodMutator } from "@/mutators/reviewPeriod/ReviewPeriodMutator"
import { TenancyMutator } from "@/mutators/tenancy/TenancyMutator"
import { getAccountHealthChecks } from "@/services-handlers/getAccountHealthChecks"
import { getAccountMeterpointAgreements } from "@/services-handlers/getAccountMeterpointAgreements"
import { getAccountMeterpointTariffs } from "@/services-handlers/getAccountMeterpointTariffs"
import { getActivityFeedEvents } from "@/services-handlers/getActivityFeedEvents"
import { getNotes } from "@/services-handlers/getNotes"
import {
  getAccountContacts,
  getPrimaryContact,
  isCustomerDeceased,
} from "@/services-handlers/getAccountContacts"
import { getJuniferTickets } from "@/services-handlers/getJuniferTickets"
import { getOpenComplaints } from "@/services-handlers/getOpenComplaints"
import { getBillEligibility } from "@/services-handlers/getBillEligibility"
import { getDebtPaymentPlanEligibility } from "@/services-handlers/getDebtPaymentPlanEligibility"
import { getImbpAvailability } from "@/services-handlers/getImbpAvailability"
import { getImbpBookings } from "@/services-handlers/getImbpBookings"
import { getMeterpointCancellationReason } from "@/services-handlers/getMeterpointCancellationReason"
import { getRefundEligibility } from "@/services-handlers/getRefundEligibility"
import {
  getSuggestedPayment,
  getSuggestedPaymentBreakdown,
} from "@/services-handlers/getSuggestedPayment"
import { getSupplyAddressAccounts } from "@/services-handlers/getSupplyAddressAccounts"
import { AccountService } from "@/services/AccountService"
import { BillRequestService } from "@/services/BillRequestService"
import { BillService } from "@/services/BillService"
import { IBill } from "@/models/bill/bill.types"
import { ITransaction } from "@/models/transaction/transaction.types"
import { IJuniferComm } from "@/models/juniferCommunication/juniferComm.types"
import { IBillRequest } from "@/models/billRequest/billRequest.types"
import { ApiError, QueryResult } from "@/api/utils/types"
import { DebtPaymentPlanService } from "@/services/DebtPaymentPlanService"
import { getDirectDebitMandates } from "@/services-handlers/getDirectDebitMandates"
import { JuniferService } from "@/services/JuniferService"
import { MeterReminderService } from "@/services/MeterReminderService"
import { MeterpointService } from "@/services/MeterpointService"
import { PaymentScheduleService } from "@/services/PaymentScheduleService"
import { PsrService } from "@/services/PsrService"
import { getUnderReviewPeriods } from "@/services-handlers/getUnderReviewPeriods"
import { TransactionService } from "@/services/TransactionService"
import { defineStore } from "pinia"

import { getAccountCreditEligibility } from "@/services-handlers/getAccountCreditEligibility"
import { cotErrorPrettifier } from "@/helpers/cotErrorPrettifier"
import { ConfigurationService } from "@/services/ConfigurationService"
import { ChangeOfTenancyResponseDto } from "@/api/backofficeRest/generated/model"
import { CardPaymentMutator } from "@/mutators/cardPayment/CardPaymentMutator"
import { WarrantsMutator } from "@/mutators/warrants/WarrantsMutator"
import { GdprFieldsService } from "@/services/GdprFieldsService"
import { getMeterAppointmentEvents } from "@/services-handlers/getMeterAppointmentEvents"
import { hasOpenComplaintOnAccount } from "@/services-handlers/hasOpenComplaintOnAccount"
import { getIhdDetails } from "@/services-handlers/getIhdDetails"
import { BulkSmartReadsUploadMutator } from "@/mutators/meterpoint/BulkSmartReadsUploadMutator"
import { getDefaultNotice } from "@/services-handlers/getDefaultNotice"
import { getWarrantStatus } from "@/services-handlers/getWarrantStatus"
import { getMeterExchangeEligibility } from "@/services-handlers/getMeterExchangeEligibility"
import { getOnlineAccountUsers } from "@/services-handlers/getOnlineAccountUsers"
import { getInvoluntaryPaygSwitchTicket } from "@/services-handlers/getInvoluntaryPaygSwitchTicket"
import { getRtsMeters } from "@/services-handlers/getRtsMeters"
import { getImbpAccount } from "@/services-handlers/getImbpAccount"
import { SmartPayAsYouGoMutator } from "@/mutators/smartPayAsYouGo/SmartPayAsYouGoMutator"
import { SmartPayAsYouGoService } from "@/services/SmartPayAsYouGoService"
import { RegistrationCreditChecksService } from "@/services/operations/RegistrationCreditChecksService"
import { getAccountAgreementsByMeterPoint } from "@/services-handlers/getAccountAgreementsByMeterPoint"
import { getCustomerPsrSettings } from "@/services-handlers/getCustomerPsrSettings"
import { getPaperBillState } from "@/services-handlers/getPaperBillState"
import { getCustomerAccountsByAccountNumber } from "@/services-handlers/getCustomerAccountsByAccountNumber"
import { IStorePsrCustomerVulnerability } from "@/mutators/psr/PsrMutator.types"
import { getImbpSmartMeterInterest } from "@/services-handlers/getImbpSmartMeterInterest"
import { getImbpPreviouslyOfferedSlots } from "@/services-handlers/getImbpPreviouslyOfferedSlots"
import { ImbpService } from "@/services/ImbpService"

export interface ICurrentAccountStore {
  accountNumber: string
  isSidePanelCollapsed: boolean
  recentTenancyChange: {
    accountNumber: string
    email: string
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
    errorMessage: string | null
  }
  recentImbpChange: {
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
  }
  imbpReservationId: string
  getImbpSlotWithCoreId: boolean
  seenOpenComplaints: boolean
  smartReadFrequencyUpdated: boolean
  bulkUploadErrors: {
    errors: IBulkSmartReadsResult[] | null
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
  }
  suggestedPaymentParams: {
    includeBalance: boolean
    includeUnbilledReadings: boolean
  }
  seenRtsMeterModal: boolean
  vulnerabilities: IStorePsrCustomerVulnerability[]
}

export const useCurrentAccountStore = defineStore("currentAccount", {
  state: () => {
    return {
      accountNumber: "",
      isSidePanelCollapsed: false,
      recentTenancyChange: {
        accountNumber: "",
        email: "",
        isLoading: false,
        isError: false,
        isSuccess: false,
        errorMessage: null,
      },
      recentImbpChange: {
        isLoading: false,
        isError: false,
        isSuccess: false,
      },
      seenOpenComplaints: false,
      imbpReservationId: "",
      getImbpSlotWithCoreId: false,
      smartReadFrequencyUpdated: false,
      bulkUploadErrors: {
        errors: null,
        isLoading: false,
        isError: false,
        isSuccess: false,
      },
      suggestedPaymentParams: {
        includeBalance: true,
        includeUnbilledReadings: true,
      },
      seenRtsMeterModal: false,
      vulnerabilities: [],
    } as ICurrentAccountStore
  },
  getters: {
    account: (state) => AccountService.getAccount(state.accountNumber),
    coreId: (state) =>
      AccountService.getCoreIdByAccountNumber(state.accountNumber),
    customerId: (state) =>
      AccountService.getCustomerIdByAccountNumber(state.accountNumber),
    customerAccounts: (state) =>
      getCustomerAccountsByAccountNumber(state.accountNumber),
    accountContacts: (state) => getAccountContacts(state.accountNumber),
    onlineAccountUsers: (state) => getOnlineAccountUsers(state.accountNumber),
    billRequest(): QueryResult<IBillRequest[]> {
      return BillRequestService.getForAccount(this.coreId.data)
    },
    bills(): QueryResult<IBill[]> {
      return BillService.getForAccount(this.coreId.data)
    },
    primaryAccountContact: (state) => getPrimaryContact(state.accountNumber),
    isCustomerDeceased: (state) => isCustomerDeceased(state.accountNumber),
    spaygEligibility: (state) =>
      SmartPayAsYouGoService.checkSpaygEligibility(state.accountNumber),
    transactions(): QueryResult<ITransaction[]> {
      return TransactionService.getForAccount(this.coreId.data)
    },
    notes: (state) => getNotes(state.accountNumber),
    juniferComms(): QueryResult<IJuniferComm[]> {
      return JuniferService.getCommsForAccount(this.coreId.data)
    },
    directDebit: (state) => getDirectDebitMandates(state.accountNumber),
    depositStatus: (state) =>
      RegistrationCreditChecksService.getCreditCheckStatus(state.accountNumber),
    depositStatusDetails: (state) =>
      RegistrationCreditChecksService.getRegistrationCreditChecksByAccountNumber(
        state.accountNumber,
      ),
    juniferTickets: (state) => getJuniferTickets(state.accountNumber),
    activityFeedEvents: (state) => getActivityFeedEvents(state.accountNumber),
    underReviewPeriods: (state) => getUnderReviewPeriods(state.accountNumber),
    meterpoints: (state) =>
      MeterpointService.getForAccount(state.accountNumber),
    unavailableMeterpoints: (state) =>
      MeterpointService.getUnavailableForAccount(state.accountNumber),
    meterpointAgreements: (state) =>
      getAccountMeterpointAgreements(state.accountNumber),
    agreementsByMeterPoint: (state) =>
      getAccountAgreementsByMeterPoint(state.accountNumber),
    supplyAddressAccounts: (state) =>
      getSupplyAddressAccounts(state.accountNumber),
    healthChecks(state) {
      return getAccountHealthChecks(state.accountNumber)
    },
    preDefaultNotice: (state) => getDefaultNotice(state.accountNumber),
    warrantStatus: (state) => getWarrantStatus(state.accountNumber),
    involuntaryPaygSwitchTicket: (state) =>
      getInvoluntaryPaygSwitchTicket(state.accountNumber),
    meterExchangeEligibility: (state) =>
      getMeterExchangeEligibility(state.accountNumber),
    latestRead: (state) =>
      MeterpointService.getLatestAccountRead(state.accountNumber),
    meterpointTariffs: (state) =>
      getAccountMeterpointTariffs(state.accountNumber),
    paymentSchedule: (state) =>
      PaymentScheduleService.getForAccount(state.accountNumber),
    getMeterpointCancellationReason: (state) =>
      getMeterpointCancellationReason(state.accountNumber),
    paymentSchedules: (state) =>
      PaymentScheduleService.getForAccount(state.accountNumber),
    currentPaymentSchedule: (state) =>
      PaymentScheduleService.getCurrentSchedule(state.accountNumber),
    nextPaymentSchedule: (state) =>
      PaymentScheduleService.getNextSchedule(state.accountNumber),
    futurePaymentSchedules: (state) =>
      PaymentScheduleService.getFutureSchedules(state.accountNumber),
    hasActivePaymentSchedule: (state) =>
      PaymentScheduleService.hasActivePaymentSchedule(state.accountNumber),
    debtPaymentPlans: (state) =>
      DebtPaymentPlanService.getForAccount(state.accountNumber),
    activeDebtPaymentPlan: (state) =>
      DebtPaymentPlanService.activeDebtPaymentPlan(state.accountNumber),
    debtPaymentPlanOptions: (state) =>
      DebtPaymentPlanService.getPlanOptionsForAccount(state.accountNumber),
    debtPaymentPlanEligibility: (state) =>
      getDebtPaymentPlanEligibility(state.accountNumber),
    renewalPeriod: () => ConfigurationService.getRenewalWindow(),
    suggestedPayment: ({ accountNumber, suggestedPaymentParams }) => {
      return (
        includeBalance = suggestedPaymentParams.includeBalance,
        includeUnbilledReadings = suggestedPaymentParams.includeUnbilledReadings,
        specifiedMeterpoints?: IMeterpoint[],
      ) =>
        getSuggestedPayment(
          accountNumber,
          includeBalance,
          includeUnbilledReadings,
          specifiedMeterpoints,
        )
    },
    suggestedPaymentBreakdown: ({ accountNumber, suggestedPaymentParams }) => {
      return (
        includeBalance = suggestedPaymentParams.includeBalance,
        includeUnbilledReadings = suggestedPaymentParams.includeUnbilledReadings,
      ) =>
        getSuggestedPaymentBreakdown(
          accountNumber,
          includeBalance,
          includeUnbilledReadings,
        )
    },
    refundEligibility: (state) => getRefundEligibility(state.accountNumber),
    imbpAccount: (state) => getImbpAccount(state.accountNumber),
    imbpAvailability: ({ accountNumber, getImbpSlotWithCoreId }) =>
      getImbpAvailability(accountNumber, getImbpSlotWithCoreId),
    imbpBookings: (state) => getImbpBookings(state.accountNumber),
    imbpSmartMeterInterest: (state) =>
      getImbpSmartMeterInterest(state.accountNumber),
    imbpPreviouslyOfferedSlots: (state) =>
      getImbpPreviouslyOfferedSlots(state.accountNumber),
    smartMeterEligibility: (state) =>
      ImbpService.getSmartMeterEligibility(state.accountNumber),
    imbpCancellationReasons: () => ImbpService.getCancellationReasons(),
    billEligibility: (state) => getBillEligibility(state.accountNumber),
    meterReminders: (state) =>
      MeterReminderService.getForAccount(state.accountNumber),
    accountCreditEligibility: (state) =>
      getAccountCreditEligibility(state.accountNumber),
    openComplaints: (state) => getOpenComplaints(state.accountNumber),
    meterAppointmentEvents: (state) =>
      getMeterAppointmentEvents(state.accountNumber),
    gdprFields: (state) => GdprFieldsService.getGdprFields(state.accountNumber),
    hasOpenComplaint: (state) => hasOpenComplaintOnAccount(state.accountNumber),
    ihdDetails: (state) => getIhdDetails(state.accountNumber),
    rtsMeters: (state) => getRtsMeters(state.accountNumber),
    allVulnerabilities: () => PsrService.getVulnerabilities(),
    customerSettings: (state) => getCustomerPsrSettings(state.accountNumber),
    paperBillState: (state) => getPaperBillState(state.accountNumber),
  },
  actions: {
    getActiveDrawInfoForAccount(billingAccountId: number) {
      return AccountService.getActiveDrawInfoForAccount(billingAccountId)
    },
    submitReading() {
      console.log("reading submitted")
    },
    setVulnerabilities(vulnerabilities: IStorePsrCustomerVulnerability[]) {
      this.vulnerabilities = vulnerabilities
    },
    setSidePanelCollapsed(value: boolean) {
      this.isSidePanelCollapsed = value
    },
    submitChangeOfTenancy() {
      const onSuccess = (response: ChangeOfTenancyResponseDto) => {
        this.recentTenancyChange = {
          accountNumber: response.occupierAccountNumber ?? "",
          email: response.contacts?.[0]?.email ?? "",
          isLoading: false,
          isError: false,
          isSuccess: true,
          errorMessage: "",
        }
      }
      const onError = (error: ApiError) => {
        this.recentTenancyChange = {
          ...this.recentTenancyChange,
          isLoading: false,
          isError: true,
          isSuccess: false,
          errorMessage: cotErrorPrettifier(error.message, true),
        }
      }
      this.recentTenancyChange.isLoading = true
      return TenancyMutator.createChangeOfTenancy(onSuccess, onError)
    },
    submitSmartPayAsYouGo() {
      return SmartPayAsYouGoMutator.submit()
    },
    submitIssueBill() {
      return BillMutator.issueBill(this.account.data.juniferId)
    },
    addNote(invalidateCache = true) {
      return NoteMutator.addNote(invalidateCache)
    },
    addContact() {
      return ContactMutator.addForAccount()
    },
    updateContact() {
      return ContactMutator.updateForAccount()
    },
    updateContactComms() {
      return ContactMutator.updateCommsForAccount()
    },
    removeContact() {
      return ContactMutator.removeForAccount()
    },
    updateJuniferTicket() {
      return JuniferTicketMutator.updateForAccount()
    },
    addReviewPeriod() {
      return ReviewPeriodMutator.addForAccount()
    },
    removeReviewPeriod() {
      return ReviewPeriodMutator.removeForAccount()
    },
    editReviewPeriod() {
      return ReviewPeriodMutator.editForAccount()
    },
    submitReadings(
      ...args: Parameters<typeof MeterReadingsMutator.submitForAccount>
    ) {
      return MeterReadingsMutator.submitForAccount(...args)
    },
    openJuniferUI() {
      return JuniferMutator.openUI()
    },
    updateConsent() {
      const onSuccessFn = () => {
        this.smartReadFrequencyUpdated = true
      }

      return ConsentMutator.updateSmartRead(onSuccessFn)
    },
    createPaymentSession() {
      if (!this.coreId.data) throw new Error("Missing coreId")
      return CardPaymentMutator.createPaymentSession({
        accountCoreId: this.coreId.data,
        accountNumber: this.account.data.number,
      })
    },
    reserveImbpSlot(payload) {
      const onSuccess = (response) => {
        this.imbpReservationId = response.data.reservationId
      }

      ImbpMutator.reserveSlot(onSuccess).mutate({
        ...payload,
        imbpAccount: this.imbpAccount.data,
        mop: this.smartMeterEligibility.data?.meterOperatorCode ?? undefined,
      })
    },
    performImbpMutation(mutatorMethod, payload, additionalData = {}) {
      this.recentImbpChange = {
        isLoading: true,
        isError: false,
        isSuccess: false,
      }

      const onSuccess = () => {
        this.recentImbpChange = {
          isLoading: false,
          isError: false,
          isSuccess: true,
        }
      }

      const onError = () => {
        this.recentImbpChange = {
          isLoading: false,
          isError: true,
          isSuccess: false,
        }
      }

      mutatorMethod(onSuccess, onError).mutate({
        ...payload,
        imbpAccount: this.imbpAccount.data,
        account: this.account.data,
        smartMeterEligibility: this.smartMeterEligibility.data,
        ...additionalData,
      })
    },
    submitImbpBooking(payload) {
      this.performImbpMutation(ImbpMutator.submit, payload, {
        reservationId: this.imbpReservationId,
      })
    },
    cancelImbpBooking({ bookingId }) {
      if (!this.coreId.data) throw Error("Missing core Id")
      return ImbpMutator.cancelBookings(
        this.coreId.data,
        bookingId,
        this.smartMeterEligibility.data?.meterOperatorCode ?? undefined,
      )
    },
    toggleMeterReminders(active: boolean) {
      MeterReminderService.toggleForAccount().mutate({
        accountNumber: this.accountNumber,
        isActive: active,
      })
    },
    bulkSmartReads(payload) {
      this.bulkUploadErrors = {
        ...this.bulkUploadErrors,
        isLoading: true,
        isSuccess: false,
        isError: false,
      }

      const onSuccess = (response: { bulkXritm: IBulkSmartReadsResult[] }) => {
        const errorResponse = response.bulkXritm
        this.bulkUploadErrors = {
          ...this.bulkUploadErrors,
          isLoading: false,
          isSuccess: true,
          errors: errorResponse.length ? errorResponse : null,
        }
      }
      const onError = () => {
        this.bulkUploadErrors = {
          ...this.bulkUploadErrors,
          isLoading: false,
          isSuccess: false,
          isError: true,
        }
      }

      BulkSmartReadsUploadMutator.uploadBulkFile(onSuccess, onError).mutate(
        payload,
      )
    },
    removeFromInvoluntaryPayg() {
      return WarrantsMutator.removeFromInvoluntaryPayg()
    },
    addToMeterExchangeJourney() {
      return WarrantsMutator.addToMeterExchangeJourney()
    },
    removeFromMeterExchangeJourney() {
      return WarrantsMutator.removeFromMeterExchangeJourney()
    },
  },
})

export type CurrentAccountStore = ReturnType<typeof useCurrentAccountStore>
export type CurrentAccountTestStore = RemoveReadonly<
  ReturnType<typeof useCurrentAccountStore>
>
